import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, NgZone, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, ParamMap, Router } from '@angular/router';
// ...existing code...
import { Client, ClientPayload, ClientsService, Region, Territoire } from '../../../services/clients.service';

@Component({
  selector: 'app-clients',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './clients.component.html',
  styleUrl: './clients.component.scss'
})
export class ClientsComponent implements OnInit {
  protected pageMode: 'gerer' | 'voir' | 'creer' | 'modifier' | 'supprimer' = 'gerer';
  protected clients: Client[] = [];
  protected regions: Region[] = [];
  protected territoires: Territoire[] = [];
  protected selectedId = '';
  protected loading = false;
  protected loadingList = false;
  protected loadingReferenceLists = false;
  protected deletingClientId: number | null = null;
  protected success = '';
  protected error = '';

  // Champs du formulaire
  protected identifiant = '';
  protected urlSiteWeb = '';
  protected interne = false;
  protected businessUnit = '';
  protected siteOuContrat = ''

  constructor(
    private readonly clientsService: ClientsService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly zone: NgZone,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.route.queryParamMap.subscribe((params: ParamMap) => {
      const idParam = params.get('id');
      if (idParam && !Number.isNaN(Number(idParam))) {
        this.selectedId = idParam;
        this.onSelectChange();
      }
    });

    this.syncPageMode();
    this.router.events.subscribe((event: unknown) => {
      if (event instanceof NavigationEnd) {
        this.syncPageMode();
        this.loadClients();
      }
    });
    this.loadClients();
  }

  protected get showCreateSection(): boolean {
    return this.pageMode === 'gerer' || this.pageMode === 'creer';
  }

  protected get showEditSection(): boolean {
    return this.pageMode === 'gerer' || this.pageMode === 'modifier';
  }

  protected get showListSection(): boolean {
    return this.pageMode === 'gerer' || this.pageMode === 'voir' || this.pageMode === 'supprimer';
  }



  protected get showDeleteActionsInTable(): boolean {
    return this.pageMode === 'gerer' || this.pageMode === 'voir' || this.pageMode === 'supprimer';
  }

  protected get showRowActionsInTable(): boolean {
    return this.pageMode === 'gerer' || this.pageMode === 'voir' || this.pageMode === 'supprimer';
  }

  private syncPageMode(): void {
    const urlPath = this.router.url.split('?')[0] ?? '';
    const lastPart = urlPath.split('/').pop() ?? '';
    if (['voir', 'creer', 'modifier', 'supprimer'].includes(lastPart)) {
      this.pageMode = lastPart as 'voir' | 'creer' | 'modifier' | 'supprimer';
    } else {
      this.pageMode = 'gerer';
    }
  }



  private loadClients(): void {
    this.loadingList = true;
    this.zone.run(() => {
      this.clientsService.list().subscribe({
        next: (clients: Client[]) => {
          this.clients = clients;
          if (this.selectedId) {
            this.onSelectChange();
          }
          this.loadingList = false;
          this.cdr.detectChanges();
        },
        error: (err: HttpErrorResponse) => {
          this.error = `Erreur lors du chargement des clients: ${this.getErrorMessage(err)}`;
          this.loadingList = false;
          this.cdr.detectChanges();
        }
      });
    });
  }

  

  protected onSelectChange(): void {
    const selected = this.clients.find((c) => c.id === Number(this.selectedId));
    if (selected) {
      this.identifiant = selected.identifiant ?? '';
      this.urlSiteWeb = selected.url_site_web ?? '';
      this.interne = Boolean(selected.interne);
      this.siteOuContrat = selected.siteOuContrat ?? '';
      this.businessUnit = selected.businessUnit ?? '';
      this.cdr.detectChanges();
    }
  }

  protected editClient(client: Client): void {
    this.selectedId = String(client.id);
    this.onSelectChange();
    const modifierPath = this.router.url.includes('/gestion-stocks/clients/')
      ? '/intranet/gestion-stocks/clients/modifier'
      : '/intranet/gestion-clients/contacts/modifier';
    void this.router.navigate([modifierPath], {
      queryParams: { id: client.id }
    });
  }

  protected onInterneChange(): void {
    if (!this.interne) {
      this.siteOuContrat = '';
      this.businessUnit = '';
    }
    this.cdr.detectChanges();
  }

  protected getRegionNom(regionId: number | null): string {
    if (regionId === null) return '—';
    return this.regions.find((r) => r.id === regionId)?.nom ?? '—';
  }

  protected getTerritoireNom(territoireId: number | null): string {
    if (territoireId === null) return '—';
    return this.territoires.find((t) => t.id === territoireId)?.nom ?? '—';
  }

  protected createClient(): void {
    if (!this.identifiant.trim()) {
      this.error = "L'identifiant est requis.";
      return;
    }

    this.loading = true;
    this.error = '';
    this.success = '';

    const payload: ClientPayload = {
      identifiant: this.identifiant.trim(),
      url_site_web: this.urlSiteWeb.trim() || undefined,
      interne: this.interne,
      siteOuContrat: this.interne ? (this.siteOuContrat.trim() || null) : null,
      businessUnit: this.interne ? (this.businessUnit.trim() || null) : null,
    };

    this.zone.run(() => {
      this.clientsService.create(payload).subscribe({
        next: () => {
          this.success = 'Client créé avec succès.';
          this.clearCreateForm();
          this.loadClients();
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: (err: HttpErrorResponse) => {
          this.error = `Erreur lors de la création: ${this.getErrorMessage(err)}`;
          this.loading = false;
          this.cdr.markForCheck();
        }
      });
    });
  }

  protected updateClient(): void {
    if (!this.selectedId) {
      this.error = 'Veuillez sélectionner un client.';
      return;
    }
    if (!this.identifiant.trim()) {
      this.error = "L'identifiant est requis.";
      return;
    }

    this.loading = true;
    this.error = '';
    this.success = '';

    const payload: ClientPayload = {
      identifiant: this.identifiant.trim(),
      url_site_web: this.urlSiteWeb.trim() || undefined,
      interne: this.interne,
      siteOuContrat: this.interne ? (this.siteOuContrat.trim() || null) : null,
      businessUnit: this.interne ? (this.businessUnit.trim() || null) : null,
    };

    this.zone.run(() => {
      this.clientsService.update(Number(this.selectedId), payload).subscribe({
        next: () => {
          const updatedClientId = this.selectedId;
          this.success = 'Client modifié avec succès.';
          this.loadClients();
          this.selectedId = updatedClientId;
          this.onSelectChange();
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: (err: HttpErrorResponse) => {
          this.error = `Erreur lors de la modification: ${this.getErrorMessage(err)}`;
          this.loading = false;
          this.cdr.markForCheck();
        }
      });
    });
  }

  protected deleteClient(): void {
    if (!this.selectedId) {
      this.error = 'Veuillez sélectionner un client.';
      return;
    }

    const clientId = Number(this.selectedId);
    const selected = this.clients.find((c) => c.id === clientId);
    const label = selected?.identifiant ?? `#${clientId}`;

    if (!confirm(`Êtes-vous sûr de vouloir supprimer le client "${label}" ?`)) {
      return;
    }

    this.loading = true;
    this.error = '';
    this.success = '';

    this.zone.run(() => {
      this.clientsService.delete(clientId).subscribe({
        next: () => {
          this.success = 'Client supprimé avec succès.';
          this.loadClients();
          this.selectedId = '';
          this.clearEditForm();
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: (err: HttpErrorResponse) => {
          this.error = `Erreur lors de la suppression: ${this.getErrorMessage(err)}`;
          this.loading = false;
          this.cdr.markForCheck();
        }
      });
    });
  }

  protected deleteClientRow(client: Client): void {
    if (!confirm(`Êtes-vous sûr de vouloir supprimer le client "${client.identifiant}" ?`)) {
      return;
    }

    this.deletingClientId = client.id;

    this.zone.run(() => {
      this.clientsService.delete(client.id).subscribe({
        next: () => {
          this.success = 'Client supprimé avec succès.';
          this.loadClients();
          this.deletingClientId = null;
          this.cdr.markForCheck();
        },
        error: (err: HttpErrorResponse) => {
          this.error = `Erreur lors de la suppression: ${this.getErrorMessage(err)}`;
          this.deletingClientId = null;
          this.cdr.markForCheck();
        }
      });
    });
  }


  private clearCreateForm(): void {
    this.identifiant = '';
    this.urlSiteWeb = '';
    this.interne = false;
    this.siteOuContrat = '';
    this.businessUnit = '';
  }

  private clearEditForm(): void {
    this.identifiant = '';
    this.urlSiteWeb = '';
    this.interne = false;
    this.siteOuContrat = '';
    this.businessUnit = '';
  }

  private getErrorMessage(err: HttpErrorResponse): string {
    if (typeof err.error === 'string' && err.error.trim()) return err.error;
    if (err.error?.detail) return err.error.detail;
    if (err.error?.message) return err.error.message;
    return err.statusText || 'Erreur inconnue';
  }
}
